import React, { useEffect, useState } from 'react'
import { Layout } from '../layout/Layout'
import { Table, Button, Modal, Form } from 'react-bootstrap';

const API_URL = process.env.REACT_APP_API_URL;

const Pricing = () => {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(false);

    // For editing
    const [editPlanId, setEditPlanId] = useState(null);
    const [editActualPrice, setEditActualPrice] = useState('');
    const [editDiscountPrice, setEditDiscountPrice] = useState('');

    // For adding new plan
    const [showAddModal, setShowAddModal] = useState(false);
    const [newPlanType, setNewPlanType] = useState('');
    const [newActualPrice, setNewActualPrice] = useState('');
    const [newDiscountPrice, setNewDiscountPrice] = useState('');

    // Fetch all plans
    const fetchPlans = async () => {
        try {
            const res = await fetch(`${API_URL}/pricing/all`);
            const data = await res.json();
            setPlans(data);
        } catch (err) {
            console.error("Error fetching pricing:", err);
        }
    };

    useEffect(() => {
        fetchPlans();
    }, []);

    // Edit plan inline
    const handleEdit = (plan) => {
        setEditPlanId(plan.pricingId);
        setEditActualPrice(plan.actualPrice);
        setEditDiscountPrice(plan.discountPrice);
    };

    const handleSave = async (planId) => {
        try {
            setLoading(true);
            const res = await fetch(`${API_URL}/pricing/update/${planId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    actualPrice: editActualPrice,
                    discountPrice: editDiscountPrice
                })
            });
            if (!res.ok) throw new Error('Failed to update plan');
            setEditPlanId(null);
            fetchPlans();
        } catch (err) {
            alert(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Add new plan
    const handleAddPlan = async () => {
        if (!newPlanType || !newActualPrice || !newDiscountPrice) return;
        try {
            setLoading(true);
            const res = await fetch(`${API_URL}/pricing/add`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    pricingPlan: newPlanType,
                    actualPrice: newActualPrice,
                    discountPrice: newDiscountPrice
                })
            });
            if (!res.ok) throw new Error('Failed to add plan');
            setShowAddModal(false);
            setNewPlanType('');
            setNewActualPrice('');
            setNewDiscountPrice('');
            fetchPlans();
        } catch (err) {
            alert(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
       <Layout>
    <div className="plans-page">
        <div className="plans-container">

            {/* Heading */}
            <div className="plans-heading-row">
                <div>
                    <h2 className="plans-title">Pricing Plans</h2>
                    <p className="plans-subtitle">
                        {plans.length} {plans.length === 1 ? "plan" : "plans"} available
                    </p>
                </div>
                <button className="plans-btn plans-btn--solid" onClick={() => setShowAddModal(true)}>
                    + Add Plan
                </button>
            </div>

            {/* Card */}
            <div className="plans-card">
                <div className="plans-card-header">
                    <h5 className="mb-0">Available Plans</h5>
                </div>

                <div className="table-responsive">
                    <Table className="plans-table mb-0 align-middle">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Plan</th>
                                <th>Actual Price</th>
                                <th>Discount Price</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {plans.length > 0 ? (
                                plans.map((plan) => (
                                    <tr key={plan.pricingId}>
                                        <td className="plans-id">#{plan.pricingId}</td>
                                        <td className="fw-semibold">{plan.pricingPlan}</td>

                                        <td>
                                            {editPlanId === plan.pricingId ? (
                                                <Form.Control
                                                    type="number"
                                                    value={editActualPrice}
                                                    onChange={e => setEditActualPrice(e.target.value)}
                                                    className="plans-input"
                                                />
                                            ) : (
                                                <span className="plans-price-strike">
                                                    ₹{plan.actualPrice}
                                                </span>
                                            )}
                                        </td>

                                        <td>
                                            {editPlanId === plan.pricingId ? (
                                                <Form.Control
                                                    type="number"
                                                    value={editDiscountPrice}
                                                    onChange={e => setEditDiscountPrice(e.target.value)}
                                                    className="plans-input"
                                                />
                                            ) : (
                                                <span className="plans-pill">
                                                    ₹{plan.discountPrice}
                                                </span>
                                            )}
                                        </td>

                                        <td>
                                            {editPlanId === plan.pricingId ? (
                                                <div className="d-flex gap-2">
                                                    <button
                                                        className="plans-btn plans-btn--success plans-btn--sm"
                                                        onClick={() => handleSave(plan.pricingId)}
                                                        disabled={loading}
                                                    >
                                                        Save
                                                    </button>
                                                    <button
                                                        className="plans-btn plans-btn--ghost plans-btn--sm"
                                                        onClick={() => setEditPlanId(null)}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    className="plans-btn plans-btn--outline plans-btn--sm"
                                                    onClick={() => handleEdit(plan)}
                                                >
                                                    Edit
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="plans-empty-cell">
                                        No pricing plans available
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </div>
        </div>

        {/* Add Plan Modal */}
        <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered className="plans-modal">
            <Modal.Header closeButton>
                <Modal.Title>Add New Plan</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Plan Type</Form.Label>
                        <Form.Control
                            type="text"
                            value={newPlanType}
                            onChange={e => setNewPlanType(e.target.value)}
                            placeholder="Enter plan name e.g., Silver"
                            className="plans-input"
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Actual Price</Form.Label>
                        <Form.Control
                            type="number"
                            value={newActualPrice}
                            onChange={e => setNewActualPrice(e.target.value)}
                            placeholder="Enter actual price"
                            className="plans-input"
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Discount Price</Form.Label>
                        <Form.Control
                            type="number"
                            value={newDiscountPrice}
                            onChange={e => setNewDiscountPrice(e.target.value)}
                            placeholder="Enter discount price"
                            className="plans-input"
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <button className="plans-btn plans-btn--ghost" onClick={() => setShowAddModal(false)}>
                    Cancel
                </button>
                <button className="plans-btn plans-btn--solid" onClick={handleAddPlan} disabled={loading}>
                    Add Plan
                </button>
            </Modal.Footer>
        </Modal>
    </div>

    <style>{`
        .plans-page {
            min-height: 100vh;
            background: #F7F8F6;
            font-family: 'Inter', sans-serif;
            padding: 32px 0;
        }

        .plans-container {
            max-width: 1180px;
            width: 100%;
            margin: 0 auto;
            padding: 0 24px;
        }

        .plans-heading-row {
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            margin-bottom: 22px;
            gap: 16px;
            flex-wrap: wrap;
        }

        .plans-title {
            font-weight: 700;
            font-size: 24px;
            color: #1C2420;
            margin: 0;
        }

        .plans-subtitle {
            font-size: 13.5px;
            color: #8B978F;
            margin: 4px 0 0;
        }

        .plans-card {
            background: #fff;
            border-radius: 16px;
            border: 1px solid #EDEFEA;
            box-shadow: 0 1px 2px rgba(16, 24, 20, 0.03);
            overflow: hidden;
        }

        .plans-card-header {
            padding: 16px 20px;
            background: rgba(224, 123, 57, 0.06);
            border-bottom: 1px solid #F0E4D8;
        }

        .plans-card-header h5 {
            font-weight: 700;
            font-size: 15px;
            color: #B8632C;
        }

        .plans-table thead th {
            font-size: 11.5px;
            text-transform: uppercase;
            letter-spacing: 0.06em;
            font-weight: 700;
            color: #B8632C;
            padding: 14px 20px;
            border-bottom: 1px solid #F2F3F0;
            white-space: nowrap;
        }

        .plans-table tbody td {
            padding: 14px 20px;
            font-size: 14px;
            color: #333B36;
            border-bottom: 1px solid #F2F3F0;
            vertical-align: middle;
        }

        .plans-table tbody tr:last-child td {
            border-bottom: none;
        }

        .plans-table tbody tr:hover {
            background: #FBFAF8;
        }

        .plans-id {
            font-weight: 600;
            color: #8B978F;
        }

        .plans-price-strike {
            color: #A9B3AC;
            text-decoration: line-through;
        }

        .plans-pill {
            display: inline-block;
            padding: 4px 11px;
            border-radius: 999px;
            font-size: 13px;
            font-weight: 700;
            background: rgba(47, 158, 106, 0.12);
            color: #21875A;
        }

        .plans-empty-cell {
            text-align: center;
            padding: 56px 20px;
            color: #8B978F;
        }

        .plans-btn {
            font-size: 13px;
            font-weight: 600;
            padding: 8px 16px;
            border-radius: 8px;
            border: 1.5px solid transparent;
            cursor: pointer;
            transition: all 0.15s ease;
            white-space: nowrap;
        }

        .plans-btn--sm { padding: 6px 12px; font-size: 12.5px; }

        .plans-btn--solid {
            background: #e07b39;
            color: #fff;
        }
        .plans-btn--solid:hover { background: #c96a2d; }
        .plans-btn--solid:disabled { opacity: 0.55; cursor: not-allowed; }

        .plans-btn--outline {
            background: #fff;
            border-color: #e07b39;
            color: #e07b39;
        }
        .plans-btn--outline:hover { background: rgba(224, 123, 57, 0.08); }

        .plans-btn--success {
            background: rgba(47, 158, 106, 0.12);
            color: #21875A;
        }
        .plans-btn--success:hover { background: rgba(47, 158, 106, 0.2); }
        .plans-btn--success:disabled { opacity: 0.55; cursor: not-allowed; }

        .plans-btn--ghost {
            background: transparent;
            color: #5C6862;
            border-color: #E2E5E0;
        }
        .plans-btn--ghost:hover { background: #F5F6F4; }

        .plans-input {
            border-radius: 8px !important;
            border: 1.5px solid #E2E5E0 !important;
        }
        .plans-input:focus {
            border-color: #e07b39 !important;
            box-shadow: 0 0 0 3px rgba(224, 123, 57, 0.12) !important;
        }

        .plans-modal .modal-content {
            border-radius: 16px;
            border: none;
        }

        .plans-modal .modal-header {
            background: rgba(224, 123, 57, 0.06);
            border-bottom: 1px solid #F0E4D8;
        }

        .plans-modal .modal-title {
            color: #B8632C;
            font-weight: 700;
            font-size: 18px;
        }

        .plans-modal .modal-footer {
            border-top: 1px solid #F2F3F0;
        }
    `}</style>
</Layout>
    )
}

export default Pricing;